import React, { useMemo } from 'react';
import { formatTime, formatDate } from '@/utils/timezones';
import type { ClockFormat, ClockFaceStyle, DigitalFontStyle, FontSize } from '@/types/clock';

interface ModernDigitalClockProps {
  time: Date;
  format: ClockFormat;
  showSeconds?: boolean;
  showDate?: boolean;
  showTimeZone?: boolean;
  timeZoneName?: string;
  offset?: string;
  textColor?: string;
  accentColor?: string;
  style: ClockFaceStyle;
  fontStyle: DigitalFontStyle;
  fontSize: FontSize;
}

// Refined font sizes for better space utilization
const getFontSizeClasses = (size: FontSize, element: 'time' | 'date' | 'label' | 'seconds') => {
  const sizeMap = {
    time: {
      small: 'text-5xl',
      medium: 'text-6xl',
      large: 'text-7xl',
      'extra-large': 'text-8xl',
    },
    seconds: {
      small: 'text-2xl',
      medium: 'text-3xl',
      large: 'text-4xl',
      'extra-large': 'text-5xl',
    },
    date: {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg',
      'extra-large': 'text-xl',
    },
    label: {
      small: 'text-xs',
      medium: 'text-sm',
      large: 'text-base',
      'extra-large': 'text-lg',
    },
  };
  return sizeMap[element][size];
};

// Map font styles to Fontshare fonts
const getFontFamilyStyle = (fontStyle: DigitalFontStyle): string => {
  const fontMap: Record<DigitalFontStyle, string> = {
    inter: 'font-satoshi',      // Satoshi - Geometric, modern
    mono: 'font-silkscreen',    // Silkscreen - Digital/pixel
    display: 'font-clash-display', // Clash Display - Bold character
    condensed: 'font-switzer',  // Switzer - Swiss grotesque
    rounded: 'font-chillax',    // Chillax - Rounded, playful
  };
  return fontMap[fontStyle];
};

export const ModernDigitalClock: React.FC<ModernDigitalClockProps> = React.memo(({
  time,
  format,
  showSeconds = true,
  showDate = true,
  showTimeZone = true,
  timeZoneName,
  offset,
  textColor = 'text-white',
  accentColor = '#fbbf24',
  style,
  fontStyle,
  fontSize,
}) => {
  const timeString = useMemo(() => formatTime(time, format, showSeconds), [time, format, showSeconds]);
  const dateString = useMemo(() => formatDate(time, 'short'), [time]);
  
  const fontFamily = getFontFamilyStyle(fontStyle);
  const timeFontSize = getFontSizeClasses(fontSize, 'time');
  const secondsFontSize = getFontSizeClasses(fontSize, 'seconds');
  const dateFontSize = getFontSizeClasses(fontSize, 'date');
  const labelFontSize = getFontSizeClasses(fontSize, 'label');

  // Split time into parts for creative layouts
  const [mainTime, seconds] = showSeconds ? timeString.split(/(?=:\d{2}$)/) : [timeString, ''];
  
  // MINIMAL - Zen minimalism with breathing room
  if (style === 'digital-minimal') {
    return (
      <div className="relative flex flex-col items-center justify-center h-full w-full px-12 py-8">
        {/* Subtle top accent line */}
        {showTimeZone && timeZoneName && (
          <>
            <div className="absolute top-16 left-1/2 -translate-x-1/2 w-16 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" 
                 style={{ color: accentColor }} />
            <div className={`${labelFontSize} font-light ${textColor} opacity-50 mb-8 tracking-[0.2em] uppercase`}>
              {timeZoneName}
            </div>
          </>
        )}
        
        <div className={`${timeFontSize} ${fontFamily} font-light ${textColor} tabular-nums tracking-tight leading-none`}
          style={{
            fontFeatureSettings: '"tnum"',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em'
          }}
        >
          {timeString}
        </div>
        
        {showDate && (
          <div className={`${dateFontSize} font-light ${textColor} opacity-40 mt-6 tracking-widest`}>
            {dateString.toUpperCase()}
          </div>
        )}
        
        {/* Bottom accent line */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-20"
             style={{ color: accentColor }} />
      </div>
    );
  }

  // COMPACT - Dashboard-style info density
  if (style === 'digital-compact') {
    return (
      <div className="relative flex items-center justify-between h-full w-full px-16 py-8">
        {/* Left column - Time */}
        <div className="flex flex-col items-start">
          <div className={`${timeFontSize} ${fontFamily} font-bold ${textColor} tabular-nums leading-none`}
            style={{
              fontFeatureSettings: '"tnum"',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.04em'
            }}
          >
            {mainTime}
          </div>
          {showSeconds && seconds && (
            <div className={`${secondsFontSize} ${fontFamily} font-medium ${textColor} opacity-60 mt-2 tabular-nums`}
              style={{ color: accentColor, fontFeatureSettings: '"tnum"' }}
            >
              {seconds}
            </div>
          )}
        </div>
        
        {/* Right column - Metadata */}
        <div className="flex flex-col items-end gap-2">
          {showTimeZone && timeZoneName && (
            <div className={`${labelFontSize} font-semibold ${textColor} opacity-70 tracking-wider uppercase`}>
              {timeZoneName}
            </div>
          )}
          {showDate && (
            <div className={`${dateFontSize} font-medium ${textColor} opacity-50`}>
              {dateString}
            </div>
          )}
          {offset && (
            <div className={`text-xs font-mono ${textColor} opacity-40`}>
              {offset}
            </div>
          )}
        </div>
      </div>
    );
  }

  // BOLD - Impact with sophistication
  if (style === 'digital-bold') {
    return (
      <div className="relative flex flex-col items-center justify-center h-full w-full px-10 py-8 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <div className="text-[20rem] font-black" style={{ color: accentColor }}>
            {time.getHours() % 12 || 12}
          </div>
        </div>
        
        {showTimeZone && timeZoneName && (
          <div className={`${labelFontSize} font-bold ${textColor} opacity-60 mb-4 tracking-[0.3em] uppercase`}
               style={{ textShadow: `0 0 20px ${accentColor}30` }}>
            {timeZoneName}
          </div>
        )}
        
        <div className={`relative ${timeFontSize} ${fontFamily} font-black ${textColor} tabular-nums tracking-tighter leading-none`}
          style={{
            fontFeatureSettings: '"tnum"',
            fontVariantNumeric: 'tabular-nums',
            textShadow: `0 2px 40px ${accentColor}40, 0 0 80px ${accentColor}20`,
            letterSpacing: '-0.06em'
          }}
        >
          {timeString}
        </div>
        
        {showDate && (
          <div className={`${dateFontSize} font-bold ${textColor} opacity-70 mt-5 tracking-widest uppercase`}
            style={{ color: accentColor, textShadow: `0 0 20px ${accentColor}40` }}
          >
            {dateString}
          </div>
        )}
      </div>
    );
  }

  // NEON - Cyberpunk with refined glow
  if (style === 'digital-neon') {
    return (
      <div className="relative flex flex-col items-center justify-center h-full w-full px-10 py-8 overflow-hidden">
        {/* Grid background */}
        <div className="absolute inset-0 opacity-10"
             style={{
               backgroundImage: `linear-gradient(${accentColor} 1px, transparent 1px), linear-gradient(90deg, ${accentColor} 1px, transparent 1px)`,
               backgroundSize: '30px 30px'
             }} />
        
        {showTimeZone && timeZoneName && (
          <div className={`${labelFontSize} font-bold mb-6 tracking-[0.3em] uppercase relative z-10`}
            style={{
              color: accentColor,
              textShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}, 0 0 30px ${accentColor}80`,
              animation: 'neon-flicker 4s infinite alternate'
            }}
          >
            › {timeZoneName}
          </div>
        )}
        
        <div className={`${timeFontSize} ${fontFamily} font-bold tabular-nums tracking-wide leading-none relative z-10`}
          style={{
            fontFeatureSettings: '"tnum"',
            fontVariantNumeric: 'tabular-nums',
            color: accentColor,
            textShadow: `
              0 0 5px ${accentColor},
              0 0 10px ${accentColor},
              0 0 20px ${accentColor},
              0 0 40px ${accentColor},
              0 0 80px ${accentColor}60,
              0 0 120px ${accentColor}40
            `,
            animation: 'neon-flicker 4s infinite alternate',
            letterSpacing: '0.05em'
          }}
        >
          {timeString}
        </div>
        
        {showDate && (
          <div className={`${dateFontSize} font-bold mt-6 tracking-widest uppercase relative z-10`}
            style={{
              color: accentColor,
              textShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`,
              opacity: 0.85
            }}
          >
            {dateString}
          </div>
        )}
        
        {/* Corner brackets */}
        <div className="absolute top-12 left-12 w-8 h-8 border-l-2 border-t-2 opacity-40" style={{ borderColor: accentColor }} />
        <div className="absolute top-12 right-12 w-8 h-8 border-r-2 border-t-2 opacity-40" style={{ borderColor: accentColor }} />
        <div className="absolute bottom-12 left-12 w-8 h-8 border-l-2 border-b-2 opacity-40" style={{ borderColor: accentColor }} />
        <div className="absolute bottom-12 right-12 w-8 h-8 border-r-2 border-b-2 opacity-40" style={{ borderColor: accentColor }} />
      </div>
    );
  }

  // FLIP - Elevated split-flap with depth
  if (style === 'digital-flip') {
    const timeDigits = timeString.split('').map((char, i) => ({ char, key: `${char}-${i}` }));
    
    // Calculate responsive card size based on fontSize
    const cardSizes = {
      small: { width: 'w-10', height: 'h-14', fontSize: 'text-2xl' },
      medium: { width: 'w-12', height: 'h-16', fontSize: 'text-3xl' },
      large: { width: 'w-14', height: 'h-20', fontSize: 'text-4xl' },
      'extra-large': { width: 'w-16', height: 'h-24', fontSize: 'text-5xl' },
    };
    const cardSize = cardSizes[fontSize];
    
    return (
      <div className="flex flex-col items-center justify-center h-full w-full px-12 py-10">
        {showTimeZone && timeZoneName && (
          <div className={`${labelFontSize} font-medium ${textColor} opacity-50 mb-6 tracking-widest uppercase`}>
            {timeZoneName}
          </div>
        )}
        
        {/* Centered container with max-width to prevent edge touching */}
        <div className="flex items-center justify-center gap-1.5 max-w-full">
          {timeDigits.map(({ char, key }) => (
            <div
              key={key}
              className={`${
                char === ':' ? 'w-3' : `${cardSize.width} ${cardSize.height}`
              } ${char === ':' ? 'text-2xl' : cardSize.fontSize} ${fontFamily} font-bold ${textColor} text-center rounded-lg ${
                char !== ':' ? 'bg-white/10 shadow-2xl backdrop-blur-sm border border-white/20' : ''
              } flex items-center justify-center tabular-nums relative overflow-hidden group flex-shrink-0`}
              style={{
                fontFeatureSettings: '"tnum"',
                fontVariantNumeric: 'tabular-nums',
                transform: char !== ':' ? 'perspective(1000px) rotateX(2deg)' : 'none',
                transition: 'transform 0.3s ease'
              }}
            >
              {char !== ':' && (
                <>
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
                  {/* Center split line */}
                  <div className="absolute inset-x-0 top-1/2 h-px bg-black/20" />
                </>
              )}
              <span className="relative z-10">{char}</span>
            </div>
          ))}
        </div>
        
        {showDate && (
          <div className={`${dateFontSize} font-medium ${textColor} opacity-50 mt-6 tracking-wide`}>
            {dateString}
          </div>
        )}
      </div>
    );
  }

  // SEGMENTED - Refined LED with ambient glow
  if (style === 'digital-segmented') {
    return (
      <div className="relative flex flex-col items-center justify-center h-full w-full px-10 py-8 overflow-hidden">
        {/* Subtle radial glow from center */}
        <div className="absolute inset-0 bg-radial-gradient opacity-30"
             style={{
               background: `radial-gradient(circle at center, ${accentColor}10 0%, transparent 70%)`
             }} />
        
        {showTimeZone && timeZoneName && (
          <div className={`${labelFontSize} font-mono ${textColor} opacity-40 mb-8 tracking-[0.25em] uppercase`}>
            {timeZoneName}
          </div>
        )}
        
        <div className={`${timeFontSize} font-mono font-bold tabular-nums tracking-[0.15em] leading-none relative z-10`}
          style={{
            fontFeatureSettings: '"tnum"',
            fontVariantNumeric: 'tabular-nums',
            color: accentColor,
            textShadow: `
              0 0 10px ${accentColor}80,
              0 0 20px ${accentColor}60,
              0 0 40px ${accentColor}40,
              inset 0 0 10px ${accentColor}20
            `,
            filter: 'brightness(1.2)',
            WebkitTextStroke: `0.5px ${accentColor}40`
          }}
        >
          {timeString}
        </div>
        
        {showDate && (
          <div className={`${dateFontSize} font-mono font-medium mt-8 tracking-[0.2em] relative z-10`}
            style={{
              color: accentColor,
              opacity: 0.6,
              textShadow: `0 0 10px ${accentColor}60`
            }}
          >
            {dateString.toUpperCase()}
          </div>
        )}
        
        {/* Subtle scan line effect */}
        <div className="absolute inset-0 pointer-events-none opacity-5"
             style={{
               backgroundImage: 'linear-gradient(transparent 50%, rgba(0,0,0,0.3) 50%)',
               backgroundSize: '100% 4px'
             }} />
      </div>
    );
  }

  // Fallback to minimal
  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-8 py-8">
      <div className={`${timeFontSize} ${fontFamily} font-light ${textColor} tabular-nums tracking-tight`}
        style={{
          fontFeatureSettings: '"tnum"',
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {timeString}
      </div>
    </div>
  );
});

ModernDigitalClock.displayName = 'ModernDigitalClock';
